import { DiagramComponentInstance, ZoneValidationErrorType, ZoneValidator } from 'models';

const formatters: ZoneValidator['formatters'] = {
    [ZoneValidationErrorType.CONTROL_METHODS_NO_CONTROL_COMPONENT]: () => ({
        title: 'This zone has no control component',
        description: 'A zone needs 1 component that controls the voltage.',
        explain: `
### What this means
In every electrical zone, **one component must define the voltage level**.
Without it, the voltage in the zone is undefined.

### Why this is a problem
- Loads don’t know what voltage to expect
- Current and power cannot be determined
- The electrical behavior of the zone is ambiguous

### How to fix it
- Add **one** component that controls voltage
- Typical examples:
  - A voltage source
  - A converter operating in constant-voltage mode
        `,
        features: {
            simulations: true,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.CONTROL_METHODS_NO_CONTROL_COMPONENT_M]: () => ({
        title: 'This zone has no component setting the voltage at the midpoint (M)',
        description: 'A zone needs 1 component that controls the voltage at the midpoint (M).',
        explain: `
### What this means
Your system uses **L+, M, and L−** conductors.
The **M (midpoint)** voltage must be explicitly defined.

### Why this is a problem
- The midpoint is a reference between L+ and L−
- Without a defined M voltage:
  - Voltage splits become ambiguous
  - Loads may receive incorrect voltages

### How to fix it
- Add **one** component that defines the voltage at **M**
- Ensure no other component controls the same point
        `,
        features: {
            simulations: true,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.CONTROL_METHODS_NO_CONTROL_COMPONENT_Lp]: () => ({
        title: 'This zone has no component setting the voltage at L+',
        description: 'A zone needs 1 component that controls the voltage at L+',
        explain: `
### What this means
The **positive conductor (L+)** has no defined voltage.

### Why this is a problem
- Voltage differences cannot be calculated
- Components connected to L+ cannot operate correctly

### How to fix it
- Add **one** component that sets the voltage at L+
        `,
        features: {
            simulations: true,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.CONTROL_METHODS_NO_CONTROL_COMPONENT_Ln]: () => ({
        title: 'This zone has no component setting the voltage at L-',
        description: 'A zone needs 1 component that controls the voltage at L-.',
        explain: `
### What this means
The **negative conductor (L−)** has no defined voltage reference.

### Why this is a problem
- Voltage is always defined between two points
- Without L−, all voltages become relative and unclear

### How to fix it
- Add **one** component that defines the voltage at L−
        `,
        features: {
            simulations: true,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.CONTROL_METHODS_MULTIPLE_VOLTAGE_CONTROL_COMPONENTS]: () => ({
        title: 'This zone has multiple control components',
        description: 'A zone can only have 1 component that controls the voltage.',
        explain: `
### What this means
More than one component is trying to **control the same voltage**.

### Why this is a problem
- Voltage-controlling components will conflict
- The resulting voltage becomes physically incorrect
- In real systems, this would cause instability or damage

### How to fix it
- Keep **only one** voltage-controlling component
- Change others to non-voltage-controlling behavior
        `,
        features: {
            simulations: true,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.CONTROL_METHODS_MULTIPLE_VOLTAGE_CONTROL_COMPONENTS_Lp_M]: () => ({
        title: 'This zone has multiple components setting the voltage between L+ and M',
        description: 'A zone can only have 1 component that controls the voltage between L+ and M.',
        explain: `
### What this means
Multiple components define the voltage **between L+ and M**.

### Why this is a problem
- A voltage difference can only have **one authority**
- Multiple definitions contradict each other

### How to fix it
- Select **one** component to control L+ ↔ M
- Remove or reconfigure the others
        `,
        features: {
            simulations: true,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.CONTROL_METHODS_MULTIPLE_VOLTAGE_CONTROL_COMPONENTS_M_Ln]: () => ({
        title: 'This zone has multiple components setting the voltage between M and L-',
        description: 'A zone can only have 1 component that controls the voltage between M and L-.',
        explain: `
### What this means
The voltage between **M and L−** is controlled by more than one component.

### Why this is a problem
- The midpoint reference becomes inconsistent
- Loads may see unstable or incorrect voltages

### How to fix it
- Keep **one** component controlling M ↔ L−
        `,
        features: {
            simulations: true,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.CONTROL_METHODS_MULTIPLE_VOLTAGE_CONTROL_COMPONENTS_Lp_Ln]: () => ({
        title: 'This zone has multiple components setting the voltage between L+ and L-',
        description: 'A zone can only have 1 component that controls the voltage between L+ and L-.',
        explain: `
### What this means
Multiple components are defining the **main supply voltage**.

### Why this is a problem
- This is equivalent to connecting voltage sources together
- It leads to undefined or unsafe behavior

### How to fix it
- Keep **one primary voltage-defining component**
        `,
        features: {
            simulations: true,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.CONTROL_METHODS_NO_CV_AND_DV_COMBINED]: () => ({
        title: 'Voltage Droop Control & Constant Voltage combined',
        description: 'A zone cannot combine Voltage Droop Control (DV) and Constant Voltage (CV) components.',
        explain: `
### What this means
The zone combines:
- **Constant Voltage (CV)** control
- **Voltage Droop (DV)** control

### Why this is a problem
- CV enforces a fixed voltage
- DV intentionally allows voltage to drop with load
- These control strategies conflict

### How to fix it
- Use **only one control strategy** per zone
        `,
        features: {
            simulations: false,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.CONTROL_METHODS_NO_CV_AND_DV_COMBINED_Lp_M]: () => ({
        title: 'Voltage droop control and constant voltage control combined between L+ and M',
        description:
            'A zone cannot combine Voltage Droop Control (DV) and Constant Voltage (CV) components between L+ and M.',
        explain: `
### What this means
Between **L+ and M**, both CV and DV control methods are applied.

### Why this is a problem
- CV and DV define opposite control behavior
- Voltage regulation becomes contradictory

### How to fix it
- Choose either CV or DV for the L+ ↔ M segment
        `,
        features: {
            simulations: false,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.CONTROL_METHODS_NO_CV_AND_DV_COMBINED_M_Ln]: () => ({
        title: 'Voltage droop control and constant voltage control combined between M and L-',
        description:
            'A zone cannot combine Voltage Droop Control (DV) and Constant Voltage (CV) components between M and L-.',
        explain: `
### What this means
Between **M and L−**, both CV and DV control methods are applied.

### Why this is a problem
- These control strategies oppose each other
- Stable voltage behavior cannot be guaranteed

### How to fix it
- Use a single control method for M ↔ L−
        `,
        features: {
            simulations: false,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.CONTROL_METHODS_NO_CV_AND_DV_COMBINED_Lp_Ln]: () => ({
        title: 'Voltage droop control and constant voltage control combined between L+ and L-',
        description:
            'A zone cannot combine Voltage Droop Control (DV) and Constant Voltage (CV) components between L+ and L-.',
        explain: `
### What this means
Both CV and DV components define the voltage **between L+ and L−**.

### Why this is a problem
- Control objectives contradict each other
- The main supply voltage becomes unstable

### How to fix it
- Apply **one** control philosophy to the entire zone
        `,
        features: {
            simulations: false,
            wireSizing: false,
        },
    }),

    [ZoneValidationErrorType.VOLTAGE_LEVELS_MISMATCH]: (error) => {
        const { min, max, minComponentInstances, maxComponentInstances } = error.payload?.voltage ?? {};

        return {
            title: 'Voltage levels mismatch',
            description: `The voltage levels of this zone are not consistent. The minimum voltage${ofDesignators(minComponentInstances)} is ${min} V and the maximum voltage${ofDesignators(maxComponentInstances)} is ${max} V.`,
            explain: `
### What this means
Components in this zone expect **different voltage levels**.

### Why this is a problem
- Some components may be under- or over-voltage
- Electrical behavior becomes unreliable

### How to fix it
- Ensure all components are designed for the same voltage range
        `,
            features: {
                simulations: false,
                wireSizing: false,
            },
        };
    },

    [ZoneValidationErrorType.VOLTAGE_NOMINAL_EXCEEDS_MAXIMUM]: (error) => {
        const { nom, max, nomComponentInstances, maxComponentInstances } = error.payload?.voltage ?? {};

        return {
            title: 'Nominal voltage exceeds maximum voltage',
            description: `The nominal voltage${ofDesignators(nomComponentInstances)} is ${nom} V, which is higher than the maximum voltage${ofDesignators(maxComponentInstances)} of ${max} V.`,
            explain: `
### What this means
A component in this zone normally operates at a **voltage above the maximum** another component allows.

### Why this is a problem
- The other component is over-voltage during normal operation
- It may be damaged or shut down to protect itself

### How to fix it
- Choose components whose maximum voltage covers the nominal voltage of the zone
- Or lower the nominal voltage, for example by using fewer solar modules in series
        `,
            features: {
                simulations: false,
                wireSizing: false,
            },
        };
    },

    [ZoneValidationErrorType.CURRENT_NOMINAL_EXCEEDS_MAXIMUM]: (error) => {
        const { nom, max, nomComponentInstances, maxComponentInstances } = error.payload?.current ?? {};

        return {
            title: 'Nominal current exceeds maximum current',
            description: `The nominal current${ofDesignators(nomComponentInstances)} is ${nom} A, which is higher than the maximum current${ofDesignators(maxComponentInstances)} of ${max} A.`,
            explain: `
### What this means
A component delivers or draws a **current above the maximum** the component it is connected to allows.

### Why this is a problem
- The connected component is overloaded during normal operation
- It may overheat, trip, or be damaged

### How to fix it
- Choose a connected component with a higher maximum current
- Or lower the nominal current, for example by using fewer solar strings in parallel
        `,
            features: {
                simulations: false,
                wireSizing: false,
            },
        };
    },

    [ZoneValidationErrorType.VOLTAGE_TYPES_MISSING]: () => ({
        title: 'Voltage types missing',
        description: 'Every connection in a zone needs a voltage type',
        explain: `
### What this means
Some connections do not specify a **voltage type**.

### Why this is a problem
- Electrical behavior depends on voltage type
- Calculations differ between AC and DC systems

### How to fix it
- Assign a voltage type to every connection
        `,
        features: {
            simulations: true,
            wireSizing: true,
        },
    }),

    [ZoneValidationErrorType.VOLTAGE_TYPES_MISMATCH]: () => ({
        title: 'Voltage types mismatch',
        description: "It's best if all connections in a zone have the same voltage type",
        explain: `
### What this means
Different voltage types are mixed within the same zone.

### Why this is a problem
- AC and DC systems cannot be directly connected
- Assumptions about current and power break down

### How to fix it
- Keep voltage types consistent within a zone
        `,
        features: {
            simulations: true,
            wireSizing: true,
        },
    }),

    [ZoneValidationErrorType.GROUNDING_MISSING]: () => ({
        title: 'Grounding missing',
        description: 'Every isolated zone needs a grounding',
        explain: `
### What this means
This zone has no connection to **ground**.

### Why this is a problem
- Voltages have no absolute reference
- Fault behavior cannot be evaluated
- Safety principles are violated

### How to fix it
- Configure the **grounding** of a bus in the zone
- A bus that is explicitly configured as floating also counts as a deliberate choice
        `,
        features: {
            simulations: false,
            wireSizing: false,
            fault: true,
        },
    }),

    [ZoneValidationErrorType.GROUNDING_CONFIGURATION_MISSING]: () => ({
        title: 'Grounding needs a configuration',
        description: 'Your grounding component needs a configuration',
        explain: `
### What this means
A grounding component exists but is not fully defined.

### Why this is a problem
- Ground behavior is ambiguous
- Fault currents cannot be predicted

### How to fix it
- Define how the grounding behaves electrically
        `,
        features: {
            simulations: false,
            wireSizing: false,
            fault: true,
        },
    }),

    [ZoneValidationErrorType.GROUNDING_MULTIPLE]: () => ({
        title: 'Multiple grounded buses',
        description: 'Your zone can only have one bus with a grounding configured',
        explain: `
### What this means
More than one bus in the same isolated zone has a grounding configured.

### Why this is a problem
- Can create ground loops
- Causes unintended circulating currents

### How to fix it
- Keep the grounding configuration on **one** bus per zone
- Clear the grounding configuration on the other buses
        `,
        features: {
            simulations: false,
            wireSizing: false,
            fault: true,
        },
    }),

    [ZoneValidationErrorType.FAULT_ENDPOINT_VOLTAGE_MISSING]: () => ({
        title: 'Missing voltage in control zone',
        description: 'At least one endpoint in the control zone must have a voltage specified',
        explain: `
### What this means
No endpoint in this control zone has a **voltage** value specified.

### Why this is a problem
- Fault analysis requires voltage to determine initial conditions
- Without voltage, the electrical state of the zone cannot be established
- Simulation will fail or produce incorrect results

### How to fix it
- Specify **voltage** in the port configuration of at least one converter endpoint in this control zone
        `,
        features: {
            fault: true,
        },
    }),

    [ZoneValidationErrorType.FAULT_ENDPOINT_CAPACITANCE_MISSING]: () => ({
        title: 'Missing capacitance in control zone',
        description: 'At least one endpoint in the control zone must have a capacitance specified',
        explain: `
### What this means
No endpoint in this control zone has a **capacitance** value specified.

### Why this is a problem
- Fault analysis requires capacitance to calculate transient behavior
- Without capacitance, the fault current response cannot be determined
- Simulation will fail or produce incorrect results

### How to fix it
- Specify **capacitance** in the port specifications of at least one converter endpoint in this control zone
        `,
        features: {
            fault: true,
        },
    }),

    [ZoneValidationErrorType.FAULT_CONNECTION_ZERO_RESISTANCE]: () => ({
        title: 'Zero or missing resistance in connections',
        description: 'All connections must have resistance > 0 to prevent division by zero',
        explain: `
### What this means
Some connections have **zero resistance** or lack the data needed to calculate resistance.

### Why this is a problem
- Zero resistance causes **division by zero** in fault calculations
- Leads to infinite current values
- Simulation will crash or produce invalid results

### How to fix it
- Specify **resistance** directly in the connection, or
- Provide both **wire size** and **length** so resistance can be calculated
- Ensure resistance values are greater than zero
        `,
        features: {
            fault: true,
        },
    }),

    [ZoneValidationErrorType.FAULT_CAPACITANCE_TOO_SMALL]: () => ({
        title: 'Capacitance values too small',
        description: 'Capacitance values should be reasonable (> 1 nF) for accurate simulation',
        explain: `
### What this means
Some capacitance values are **extremely small** (< 1 nF).

### Why this is a problem
- Very small capacitances can cause numerical instability
- May indicate data entry errors (e.g., wrong units)
- Can lead to unrealistic time constants in fault analysis

### How to fix it
- Verify capacitance values and units are correct
- Ensure values are in the expected range (typically µF to mF)
- Consider if the component actually needs capacitance specified
        `,
        features: {
            fault: true,
        },
    }),
    [ZoneValidationErrorType.FAULT_CONNECTION_INDUCTANCE_MISSING]: () => ({
        title: 'Missing inductance in connections',
        description: 'Connections without an inductance are simulated with a minimal inductance',
        explain: `
### What this means
Some connections in this zone have no **inductance** specified.

### Why this is a problem
- The fault current rise time depends on the inductance of the cable
- Without it, the simulation falls back to a minimal inductance
- The simulated current rises faster than it would in reality, which overstates peak currents and understates the time protection has to react

### How to fix it
- Specify **inductance** directly in the connection, or
- Provide a **length** so the inductance can be calculated from it
        `,
        features: {
            fault: true,
        },
    }),

    [ZoneValidationErrorType.FAULT_NEGATIVE_VALUES]: (error) => {
        const designators = (error.payload?.componentInstances ?? []).map(
            (componentInstance) => componentInstance.designator,
        );
        const connectionCount = error.payload?.connections?.length ?? 0;

        const affected = [
            designators.length ? designators.join(', ') : null,
            connectionCount ? `${connectionCount} ${connectionCount === 1 ? 'connection' : 'connections'}` : null,
        ]
            .filter(Boolean)
            .join(' and ');

        return {
            title: 'Negative electrical values',
            description: `Resistance, inductance and capacitance must not be negative. Found on ${affected}.`,
            explain: `
### What this means
A connection or component port in this zone has a **negative** resistance, inductance or capacitance.

### Why this is a problem
- These quantities are physically non-negative
- The fault simulation refuses to run with a negative value
- A negative value usually indicates a sign or unit entry mistake

### How to fix it
- Check the resistance and inductance of the affected connections
- Check the capacitance, series resistance and resistance of the affected component ports
- Enter zero or a positive value
        `,
            features: {
                fault: true,
            },
        };
    },
};

const ofDesignators = (componentInstances?: DiagramComponentInstance[]) => {
    const designators = (componentInstances ?? []).map((componentInstance) => componentInstance.designator);

    return designators.length ? ` of ${designators.join(', ')}` : '';
};

export { formatters };
